import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Footer from "./Footer";
import Header from "./Header";
import config from "../../config";

const Kyc = () => {
  // =========================================================
  // STATE
  // =========================================================

  const [currentStep, setCurrentStep] = useState(1);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [customerData, setCustomerData] = useState(null);

  const [formData, setFormData] = useState({
    businessType: "",

    // Individual
    fullName: "",
    email: "",
    phone: "",
    businessName: "",
    currentAddress: "",

    // Business
    legalBusinessName: "",
    registeredBusinessType: "",
    registrationNumber: "",
    gstNumber: "",
    authorisedPersonName: "",
    authorisedPersonPhone: "",
    registeredAddress: "",

    // KYC numbers
    panNumber: "",
    aadharNumber: "",

    // Bank
    accountname: "",
    accountNumber: "",
    accountifsc: "",

    // Social links
    insta_link: "",
    facebook_link: "",
    marketplace_link: "",
    website_link: "",
    others_link: "",
  });

  const [files, setFiles] = useState({
    panFront: null,
    aadharFront: null,
    aadharBack: null,

    panFrontBiz: null,
    bizProof: null,
    authId: null,
    addrProof: null,

    gstImage: null,
  });

  // =========================================================
  // CUSTOMER ID
  // =========================================================

  const getCustomerId = () => {
    const userDetail = localStorage.getItem("ship_rocket_user");

    if (!userDetail) {
      return "";
    }

    try {
      const userData = JSON.parse(userDetail);

      return userData?.customer_id || "";
    } catch (error) {
      console.error("Invalid user data:", error);

      return "";
    }
  };

  // =========================================================
  // GET KYC DETAILS
  // =========================================================

  const getKycDetails = async () => {
    try {
      setLoading(true);
      setError("");

      const customerId = getCustomerId();

      if (!customerId) {
        setError("Customer information not found.");
        return;
      }

      const response = await axios.post(
        config.apiUrl + "/user/get_kyc_details",
        {
          customer_id: customerId,
        },
        {
          headers: config.headers,
        }
      );

      console.log("KYC DETAILS RESPONSE ===>", response.data);

      if (response.data.status !== true) {
        setError(
          response.data.message || "Unable to fetch your KYC information."
        );

        return;
      }

      const customer = response.data.output || {};

      setCustomerData(customer);

      populateForm(customer);
      determineInitialStep(customer);
    } catch (error) {
      console.error("KYC details API error ===>", error);

      setError(
        error?.response?.data?.message ||
          "Something went wrong while loading KYC details."
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // POPULATE FORM
  // =========================================================

  const populateForm = (customer) => {
    const kycDetails = customer?.kyc_details || {};

    const businessType = kycDetails?.business_type || "";

    setFormData({
      businessType,

      // Individual
      fullName: customer?.full_name || "",
      email: customer?.email || "",
      phone: customer?.mobile || "",
      businessName:
        businessType === "individual" ? customer?.company_name || "" : "",

      currentAddress:
        businessType === "individual"
          ? kycDetails?.registered_address || customer?.address || ""
          : "",

      // Business
      legalBusinessName:
        businessType === "business" ? customer?.company_name || "" : "",

      registeredBusinessType: kycDetails?.registered_business_type || "",

      registrationNumber: kycDetails?.registration_number || "",

      gstNumber: customer?.gst_number || "",

      authorisedPersonName: kycDetails?.authorised_person_name || "",

      authorisedPersonPhone: kycDetails?.authorised_person_phone || "",

      registeredAddress:
        kycDetails?.registered_address || customer?.address || "",

      // KYC
      panNumber: customer?.pan_number || "",

      aadharNumber: customer?.aadhaar_number || "",

      // Bank
      accountname: "",
      accountNumber: "",
      accountifsc: "",

      // Social
      insta_link: customer?.insta_link || "",

      facebook_link: customer?.facebook_link || "",

      marketplace_link: customer?.marketplace_link || "",

      website_link: customer?.website_link || "",

      others_link: customer?.others_link || "",
    });
  };

  // =========================================================
  // INITIAL STEP
  // =========================================================

  const determineInitialStep = (customer) => {
    /*
     * Approved
     */
    if (customer?.kyc === true) {
      setCurrentStep(4);
      return;
    }

    /*
     * Already submitted for manual review
     */
    if (customer?.alert_kyc === 1 && customer?.kyc !== true) {
      setCurrentStep(4);
      return;
    }

    /*
     * Existing business type
     */
    if (
      customer?.kyc_details?.business_type === "individual" ||
      customer?.kyc_details?.business_type === "business"
    ) {
      setCurrentStep(2);
      return;
    }

    setCurrentStep(1);
  };

  // =========================================================
  // INITIAL LOAD
  // =========================================================

  useEffect(() => {
    getKycDetails();
  }, []);

  // =========================================================
  // FORM CHANGE
  // =========================================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setError("");
  };

  // =========================================================
  // FILE CHANGE
  // =========================================================

  const handleFileChange = (e, key) => {
    const file =
      e.target.files && e.target.files.length > 0 ? e.target.files[0] : null;

    if (!file) {
      return;
    }

    setError("");

    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/pdf",
    ];

    if (!allowedTypes.includes(file.type)) {
      setError("Please upload a JPG, JPEG, PNG or PDF file.");

      e.target.value = "";

      return;
    }

    /*
     * Existing UI mentions 1MB / 2MB.
     * We enforce the same limits here.
     */

    const isBusinessDocument = key === "bizProof" || key === "addrProof";

    const maxSize = isBusinessDocument ? 2 * 1024 * 1024 : 1 * 1024 * 1024;

    if (file.size > maxSize) {
      setError(
        `File size must be less than ${isBusinessDocument ? "2MB" : "1MB"}.`
      );

      e.target.value = "";

      return;
    }

    setFiles((prev) => ({
      ...prev,
      [key]: file,
    }));
  };

  // =========================================================
  // FILE NAME
  // =========================================================

  const getFileName = (key) => {
    const file = files[key];

    if (file) {
      return file.name;
    }

    /*
     * Existing uploaded document
     */

    if (key === "panFront") {
      return customerData?.panFrontImage ? "PAN document already uploaded" : "";
    }

    if (key === "aadharFront") {
      return customerData?.aadharFrontImage
        ? "Aadhaar front already uploaded"
        : "";
    }

    if (key === "aadharBack") {
      return customerData?.aadharBackImage
        ? "Aadhaar back already uploaded"
        : "";
    }

    if (key === "panFrontBiz") {
      return customerData?.panFrontImage ? "PAN document already uploaded" : "";
    }

    if (key === "gstImage") {
      return customerData?.gstImage ? "GST document already uploaded" : "";
    }

    return "";
  };

  // =========================================================
  // SELECT BUSINESS TYPE
  // =========================================================

  const selectBusinessType = (type) => {
    setFormData((prev) => ({
      ...prev,
      businessType: type,
    }));

    setError("");
  };

  // =========================================================
  // STATUS
  // =========================================================

  const isApproved = customerData?.kyc === true;

  const isUnderReview =
    customerData?.alert_kyc === 1 && customerData?.kyc !== true;

  const headerStatus = useMemo(() => {
    if (isApproved) {
      return "Approved";
    }

    if (isUnderReview) {
      return "Under Review";
    }

    return "Pending";
  }, [isApproved, isUnderReview]);

  // =========================================================
  // STEP 1 VALIDATION
  // =========================================================

  const validateStep1 = () => {
    if (!formData.businessType) {
      setError("Please select your business type.");

      return false;
    }

    return true;
  };

  // =========================================================
  // STEP 2 VALIDATION
  // =========================================================

  const validateStep2 = () => {
    setError("");

    if (formData.businessType === "individual") {
      if (!formData.fullName.trim()) {
        setError("Please enter your full name.");
        return false;
      }

      if (!formData.email.trim()) {
        setError("Please enter your email address.");
        return false;
      }

      if (!formData.phone.trim()) {
        setError("Please enter your phone number.");
        return false;
      }

      if (!formData.currentAddress.trim()) {
        setError("Please enter your current address.");
        return false;
      }

      if (
        !formData.panNumber.trim() &&
        !files.panFront &&
        !customerData?.panFrontImage
      ) {
        setError("Please enter your PAN number and upload PAN.");
        return false;
      }

      if (!formData.aadharNumber.trim()) {
        setError("Please enter your Aadhaar number.");
        return false;
      }

      if (!files.aadharFront && !customerData?.aadharFrontImage) {
        setError("Please upload Aadhaar front.");
        return false;
      }

      if (!files.aadharBack && !customerData?.aadharBackImage) {
        setError("Please upload Aadhaar back.");
        return false;
      }
    }

    if (formData.businessType === "business") {
      if (!formData.legalBusinessName.trim()) {
        setError("Please enter the legal business name.");

        return false;
      }

      if (!formData.registeredBusinessType) {
        setError("Please select the business type.");

        return false;
      }

      if (!formData.authorisedPersonName.trim()) {
        setError("Please enter the authorised person's name.");

        return false;
      }

      if (!formData.authorisedPersonPhone.trim()) {
        setError("Please enter the authorised person's phone.");

        return false;
      }

      if (!formData.registeredAddress.trim()) {
        setError("Please enter the registered office address.");

        return false;
      }

      if (!formData.panNumber.trim()) {
        setError("Please enter the PAN number.");

        return false;
      }

      if (!files.panFrontBiz && !customerData?.panFrontImage) {
        setError("Please upload the business PAN card.");

        return false;
      }

      if (!files.bizProof && !customerData?.kyc_details?.business_proof) {
        setError("Please upload business proof.");

        return false;
      }

      if (!files.authId && !customerData?.kyc_details?.authorised_person_id) {
        setError("Please upload authorised person ID.");

        return false;
      }
    }

    return true;
  };

  // =========================================================
  // NEXT STEP
  // =========================================================

  const handleNext = () => {
    if (currentStep === 1) {
      if (!validateStep1()) {
        return;
      }

      setCurrentStep(2);

      return;
    }

    if (currentStep === 2) {
      if (!validateStep2()) {
        return;
      }

      setCurrentStep(3);

      return;
    }
  };

  // =========================================================
  // BACK
  // =========================================================

  const handleBack = () => {
    setError("");

    if (currentStep === 3) {
      setCurrentStep(2);
      return;
    }

    if (currentStep === 2) {
      setCurrentStep(1);
      return;
    }
  };

  // =========================================================
  // REVIEW DATA
  // =========================================================

  const reviewName =
    formData.businessType === "individual"
      ? formData.fullName
      : formData.authorisedPersonName;

  const reviewContact =
    formData.businessType === "individual"
      ? `${formData.email} / ${formData.phone}`
      : `${formData.authorisedPersonPhone}`;

  const reviewType =
    formData.businessType === "individual"
      ? "Individual"
      : formData.businessType === "business"
      ? "Registered Business"
      : "—";

  const hasPan = Boolean(
    files.panFront || files.panFrontBiz || customerData?.panFrontImage
  );

  const getReviewDocuments = () => {
    const documents = [];

    if (hasPan) {
      documents.push("PAN");
    }

    if (formData.businessType === "individual") {
      if (files.aadharFront || customerData?.aadharFrontImage) {
        documents.push("Aadhaar Front");
      }

      if (files.aadharBack || customerData?.aadharBackImage) {
        documents.push("Aadhaar Back");
      }
    }

    if (formData.businessType === "business") {
      if (files.bizProof || customerData?.kyc_details?.business_proof) {
        documents.push("Business Proof");
      }

      if (files.authId || customerData?.kyc_details?.authorised_person_id) {
        documents.push("Authorised Person ID");
      }

      if (files.addrProof || customerData?.kyc_details?.address_proof) {
        documents.push("Address Proof");
      }

      if (files.gstImage || customerData?.gstImage) {
        documents.push("GST");
      }
    }

    return documents.length ? documents.join(", ") : "No documents";
  };

  // =========================================================
  // SUBMIT KYC
  // =========================================================

  const submitKyc = async () => {
    try {
      setSubmitting(true);
      setError("");
      setSuccessMessage("");

      const customerId = getCustomerId();

      if (!customerId) {
        setError("Customer information not found.");

        return;
      }

      const data = new FormData();

      /*
       * Customer
       */

      data.append("customer_id", customerId);

      /*
       * Business type
       */

      data.append("businessType", formData.businessType);

      /*
       * Common
       */

      data.append("fullName", formData.fullName);

      data.append("email", formData.email);

      data.append("phone", formData.phone);

      /*
       * Individual
       */

      data.append("businessName", formData.businessName);

      data.append("currentAddress", formData.currentAddress);

      /*
       * Business
       */

      data.append("legalBusinessName", formData.legalBusinessName);

      data.append("registeredBusinessType", formData.registeredBusinessType);

      data.append("registrationNumber", formData.registrationNumber);

      data.append("authorisedPersonName", formData.authorisedPersonName);

      data.append("authorisedPersonPhone", formData.authorisedPersonPhone);

      data.append("registeredAddress", formData.registeredAddress);

      /*
       * KYC numbers
       */

      data.append("panNumber", formData.panNumber);

      data.append("aadharNumber", formData.aadharNumber);

      data.append("gstNumber", formData.gstNumber);

      /*
       * Bank
       */

      data.append("accountname", formData.accountname);

      data.append("accountNumber", formData.accountNumber);

      data.append("accountifsc", formData.accountifsc);

      /*
       * Social links
       */

      data.append("insta_link", formData.insta_link);

      data.append("facebook_link", formData.facebook_link);

      data.append("marketplace_link", formData.marketplace_link);

      data.append("website_link", formData.website_link);

      data.append("others_link", formData.others_link);

      /*
       * Files
       */

      if (files.panFront) {
        data.append("panFront", files.panFront);
      }

      if (files.aadharFront) {
        data.append("aadharFront", files.aadharFront);
      }

      if (files.aadharBack) {
        data.append("aadharBack", files.aadharBack);
      }

      if (files.panFrontBiz) {
        data.append("panFrontBiz", files.panFrontBiz);
      }

      if (files.bizProof) {
        data.append("bizProof", files.bizProof);
      }

      if (files.authId) {
        data.append("authId", files.authId);
      }

      if (files.addrProof) {
        data.append("addrProof", files.addrProof);
      }

      if (files.gstImage) {
        data.append("gstImage", files.gstImage);
      }

      const response = await axios.post(
        config.apiUrl + "/user/submit_kyc",
        data,
        {
          headers: {
            ...config.headers,

            /*
             * Do not manually set Content-Type.
             * Axios will add the multipart boundary.
             */
          },
        }
      );

      console.log("SUBMIT KYC RESPONSE ===>", response.data);

      if (response.data.status !== true) {
        setError(response.data.message || "Unable to submit KYC.");

        return;
      }

      setSuccessMessage(response.data.message || "KYC submitted successfully.");

      setCustomerData(response.data.output || {});

      /*
       * Move to status screen
       */

      setCurrentStep(4);
    } catch (error) {
      console.error("Submit KYC API error ===>", error);

      setError(
        error?.response?.data?.message ||
          "Something went wrong while submitting your KYC."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // =========================================================
  // UPLOAD COMPONENT
  // =========================================================

  const UploadBox = ({
    id,
    fileKey,
    title,
    description,
    accept,
    icon,
    optional = false,
  }) => {
    const selectedFile = files[fileKey];

    const existingFileName = getFileName(fileKey);

    return (
      <div className="col-md-6">
        <label className="kyc-form-label">
          {title} {!optional && <span className="req">*</span>}
        </label>

        <div
          className={`kyc-upload ${selectedFile ? "has-file" : ""}`}
          style={{
            position: "relative",
            cursor: "pointer",
          }}
          onClick={() => {
            document.getElementById(id)?.click();
          }}
        >
          <input
            type="file"
            id={id}
            accept={accept}
            style={{
              display: "none",
            }}
            onChange={(e) => handleFileChange(e, fileKey)}
          />

          <i className={`bi ${icon || "bi-file-earmark-image"}`}></i>

          <b>
            {selectedFile ? selectedFile.name : existingFileName || description}
          </b>

          <small>
            {selectedFile
              ? "Click to replace file"
              : optional
              ? "Optional"
              : "Click to upload"}
          </small>
        </div>
      </div>
    );
  };

  // =========================================================
  // LOADING
  // =========================================================

  if (loading) {
    return (
      <div>
        <Header>
          <div className="content">
            <div className="kyc-page">
              <div
                className="card-base kyc-panel on"
                style={{
                  display: "block",
                }}
              >
                <div className="kyc-card">
                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{
                      minHeight: "300px",
                    }}
                  >
                    <div className="text-center">
                      <div className="spinner-border" role="status"></div>

                      <div
                        style={{
                          marginTop: "12px",
                          color: "var(--t2)",
                        }}
                      >
                        Loading KYC details...
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Header>

        <Footer />
      </div>
    );
  }

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div>
      <Header>
        <div className="content">
          <div className="kyc-page">
            {/* =================================================
                TITLE
            ================================================= */}

            <div
              className="d-flex align-items-center justify-content-between flex-wrap"
              style={{
                gap: "10px",
              }}
            >
              <div>
                <div className="page-title">KYC Verification</div>

                <div className="page-sub">
                  Verify your business identity to unlock full shipping & faster
                  payouts.
                </div>
              </div>

              <div className="date-pill">
                <i
                  className="bi bi-patch-check"
                  style={{
                    color: "var(--primary)",
                  }}
                ></i>{" "}
                KYC{" "}
                <span
                  style={{
                    fontWeight: "700",
                  }}
                >
                  {headerStatus}
                </span>
              </div>
            </div>

            {/* =================================================
                ERROR
            ================================================= */}

            {error && (
              <div
                className="alert alert-danger mt-3 mb-0"
                style={{
                  fontSize: "13px",
                }}
              >
                <i className="bi bi-exclamation-circle me-2"></i>

                {error}
              </div>
            )}

            {/* =================================================
                SUCCESS
            ================================================= */}

            {successMessage && (
              <div
                className="alert alert-success mt-3 mb-0"
                style={{
                  fontSize: "13px",
                }}
              >
                <i className="bi bi-check-circle me-2"></i>

                {successMessage}
              </div>
            )}

            {/* =================================================
                STEPPER
            ================================================= */}

            {currentStep !== 4 && (
              <div className="kyc-steps" id="kycSteps">
                <div
                  className={`kyc-step ${currentStep >= 1 ? "cur" : ""} ${
                    currentStep > 1 ? "done" : ""
                  }`}
                >
                  <span className="kyc-dot">
                    {currentStep > 1 ? <i className="bi bi-check"></i> : "1"}
                  </span>

                  <div className="kyc-step-txt">
                    <b>Business Type</b>

                    <small>Individual / Business</small>
                  </div>
                </div>

                <div
                  className={`kyc-step ${currentStep >= 2 ? "cur" : ""} ${
                    currentStep > 2 ? "done" : ""
                  }`}
                >
                  <span className="kyc-dot">
                    {currentStep > 2 ? <i className="bi bi-check"></i> : "2"}
                  </span>

                  <div className="kyc-step-txt">
                    <b>Details & Upload</b>

                    <small>Form + documents</small>
                  </div>
                </div>

                <div className={`kyc-step ${currentStep >= 3 ? "cur" : ""}`}>
                  <span className="kyc-dot">3</span>

                  <div className="kyc-step-txt">
                    <b>Review & Submit</b>

                    <small>Confirm & send</small>
                  </div>
                </div>
              </div>
            )}

            {/* =================================================
                STEP 1
            ================================================= */}

            {currentStep === 1 && (
              <div className="card-base kyc-panel on" data-panel="1">
                <div className="kyc-card">
                  <div className="kyc-card-head">
                    <h4>Select Business Type</h4>

                    <p>
                      Choose the type of account you want to verify. You can
                      finish the rest later.
                    </p>
                  </div>

                  <div className="kyc-opts col2" id="bizTypeOpts">
                    {/* Individual */}

                    <button
                      type="button"
                      className={`kyc-opt ${
                        formData.businessType === "individual" ? "sel" : ""
                      }`}
                      onClick={() => selectBusinessType("individual")}
                    >
                      <span className="kyc-opt-ico">
                        <i className="bi bi-person-bounding-box"></i>
                      </span>

                      <div>
                        <b>Individual</b>

                        <small>Verify as a sole person / proprietorship</small>
                      </div>

                      <span className="kyc-opt-check">
                        <i className="bi bi-check-lg"></i>
                      </span>
                    </button>

                    {/* Business */}

                    <button
                      type="button"
                      className={`kyc-opt ${
                        formData.businessType === "business" ? "sel" : ""
                      }`}
                      onClick={() => selectBusinessType("business")}
                    >
                      <span className="kyc-opt-ico">
                        <i className="bi bi-briefcase"></i>
                      </span>

                      <div>
                        <b>Registered Business</b>

                        <small>
                          Company, LLP, Partnership or Private Limited
                        </small>
                      </div>

                      <span className="kyc-opt-check">
                        <i className="bi bi-check-lg"></i>
                      </span>
                    </button>
                  </div>

                  <div className="kyc-nav">
                    <span></span>

                    <button
                      type="button"
                      className="kyc-btn primary"
                      onClick={handleNext}
                      disabled={!formData.businessType}
                    >
                      Continue <i className="bi bi-arrow-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* =================================================
                STEP 2
            ================================================= */}

            {currentStep === 2 && (
              <div className="card-base kyc-panel on" data-panel="2">
                <div className="kyc-card">
                  <div className="kyc-card-head">
                    <h4>Details & Document Upload</h4>

                    <p>
                      Fill in the details for your{" "}
                      <b>
                        {formData.businessType === "business"
                          ? "Registered Business"
                          : "Individual"}
                      </b>{" "}
                      account and upload the required documents.
                    </p>
                  </div>

                  {/* =================================================
                      INDIVIDUAL
                  ================================================= */}

                  {formData.businessType === "individual" && (
                    <>
                      <div id="grpIndividual">
                        <div className="row g-3">
                          {/* Full Name */}

                          <div className="col-md-6">
                            <label className="kyc-form-label">
                              Full Name <span className="req">*</span>
                            </label>

                            <input
                              type="text"
                              name="fullName"
                              className="kyc-input"
                              placeholder="Enter your full name"
                              value={formData.fullName}
                              onChange={handleChange}
                            />
                          </div>

                          {/* Email */}

                          <div className="col-md-6">
                            <label className="kyc-form-label">
                              Email Address <span className="req">*</span>
                            </label>

                            <input
                              type="email"
                              name="email"
                              className="kyc-input"
                              placeholder="you@example.com"
                              value={formData.email}
                              onChange={handleChange}
                            />
                          </div>

                          {/* Phone */}

                          <div className="col-md-6">
                            <label className="kyc-form-label">
                              Phone Number <span className="req">*</span>
                            </label>

                            <input
                              type="tel"
                              name="phone"
                              className="kyc-input"
                              placeholder="+91 98765 43210"
                              value={formData.phone}
                              onChange={handleChange}
                            />
                          </div>

                          {/* Business Name */}

                          <div className="col-md-6">
                            <label className="kyc-form-label">
                              Business Name (if any)
                            </label>

                            <input
                              type="text"
                              name="businessName"
                              className="kyc-input"
                              placeholder="Enter business / shop name"
                              value={formData.businessName}
                              onChange={handleChange}
                            />
                          </div>

                          {/* Address */}

                          <div className="col-12">
                            <label className="kyc-form-label">
                              Current Address <span className="req">*</span>
                            </label>

                            <textarea
                              name="currentAddress"
                              className="kyc-textarea"
                              rows="2"
                              placeholder="Flat / Street / Area, City, State, PIN"
                              value={formData.currentAddress}
                              onChange={handleChange}
                            ></textarea>
                          </div>

                          {/* PAN Number */}

                          <div className="col-md-6">
                            <label className="kyc-form-label">
                              PAN Number <span className="req">*</span>
                            </label>

                            <input
                              type="text"
                              name="panNumber"
                              className="kyc-input"
                              placeholder="Enter PAN number"
                              value={formData.panNumber}
                              onChange={handleChange}
                              style={{
                                textTransform: "uppercase",
                              }}
                            />
                          </div>

                          {/* Aadhaar Number */}

                          <div className="col-md-6">
                            <label className="kyc-form-label">
                              Aadhaar Number <span className="req">*</span>
                            </label>

                            <input
                              type="text"
                              name="aadharNumber"
                              className="kyc-input"
                              placeholder="Enter Aadhaar number"
                              value={formData.aadharNumber}
                              onChange={handleChange}
                            />
                          </div>
                        </div>

                        <div
                          className="kyc-card-head"
                          style={{
                            marginTop: "22px",
                          }}
                        >
                          <h4>Identity Documents</h4>

                          <p>
                            Upload a clear copy of your documents. Our team will
                            verify them manually.
                          </p>
                        </div>

                        <div className="row g-3">
                          <UploadBox
                            id="panFront"
                            fileKey="panFront"
                            title="PAN Card"
                            description="Front side of PAN"
                            accept=".jpg,.jpeg,.png"
                            icon="bi-file-earmark-image"
                          />

                          <UploadBox
                            id="aadharFront"
                            fileKey="aadharFront"
                            title="Aadhaar Front"
                            description="Upload Aadhaar front"
                            accept=".jpg,.jpeg,.png"
                            icon="bi-person-vcard"
                          />

                          <UploadBox
                            id="aadharBack"
                            fileKey="aadharBack"
                            title="Aadhaar Back"
                            description="Upload Aadhaar back"
                            accept=".jpg,.jpeg,.png"
                            icon="bi-person-vcard"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* =================================================
                      BUSINESS
                  ================================================= */}

                  {formData.businessType === "business" && (
                    <>
                      <div id="grpBusiness">
                        <div className="row g-3">
                          {/* Legal Business Name */}

                          <div className="col-md-6">
                            <label className="kyc-form-label">
                              Legal Business Name <span className="req">*</span>
                            </label>

                            <input
                              type="text"
                              name="legalBusinessName"
                              className="kyc-input"
                              placeholder="Registered business name"
                              value={formData.legalBusinessName}
                              onChange={handleChange}
                            />
                          </div>

                          {/* Business Type */}

                          <div className="col-md-6">
                            <label className="kyc-form-label">
                              Business Type <span className="req">*</span>
                            </label>

                            <select
                              name="registeredBusinessType"
                              className="kyc-select"
                              value={formData.registeredBusinessType}
                              onChange={handleChange}
                            >
                              <option value="">Select business type</option>

                              <option value="Proprietorship">
                                Proprietorship
                              </option>

                              <option value="Partnership">Partnership</option>

                              <option value="LLP">LLP</option>

                              <option value="Private Limited">
                                Private Limited
                              </option>

                              <option value="Public Limited">
                                Public Limited
                              </option>
                            </select>
                          </div>

                          {/* Registration */}

                          <div className="col-md-6">
                            <label className="kyc-form-label">
                              CIN / Registration No.
                            </label>

                            <input
                              type="text"
                              name="registrationNumber"
                              className="kyc-input"
                              placeholder="Company registration number"
                              value={formData.registrationNumber}
                              onChange={handleChange}
                            />
                          </div>

                          {/* GST */}

                          <div className="col-md-6">
                            <label className="kyc-form-label">GST Number</label>

                            <input
                              type="text"
                              name="gstNumber"
                              className="kyc-input"
                              placeholder="22AAAAA0000A1Z5"
                              value={formData.gstNumber}
                              onChange={handleChange}
                              style={{
                                textTransform: "uppercase",
                              }}
                            />
                          </div>

                          {/* Authorised Person */}

                          <div className="col-md-6">
                            <label className="kyc-form-label">
                              Authorised Person Name{" "}
                              <span className="req">*</span>
                            </label>

                            <input
                              type="text"
                              name="authorisedPersonName"
                              className="kyc-input"
                              placeholder="Contact person name"
                              value={formData.authorisedPersonName}
                              onChange={handleChange}
                            />
                          </div>

                          {/* Authorised Phone */}

                          <div className="col-md-6">
                            <label className="kyc-form-label">
                              Authorised Person Phone{" "}
                              <span className="req">*</span>
                            </label>

                            <input
                              type="tel"
                              name="authorisedPersonPhone"
                              className="kyc-input"
                              placeholder="+91 98765 43210"
                              value={formData.authorisedPersonPhone}
                              onChange={handleChange}
                            />
                          </div>

                          {/* Registered Address */}

                          <div className="col-12">
                            <label className="kyc-form-label">
                              Registered Office Address{" "}
                              <span className="req">*</span>
                            </label>

                            <textarea
                              name="registeredAddress"
                              className="kyc-textarea"
                              rows="2"
                              placeholder="Flat / Street / Area, City, State, PIN"
                              value={formData.registeredAddress}
                              onChange={handleChange}
                            ></textarea>
                          </div>

                          {/* PAN */}

                          <div className="col-md-6">
                            <label className="kyc-form-label">
                              PAN Number <span className="req">*</span>
                            </label>

                            <input
                              type="text"
                              name="panNumber"
                              className="kyc-input"
                              placeholder="Enter PAN number"
                              value={formData.panNumber}
                              onChange={handleChange}
                              style={{
                                textTransform: "uppercase",
                              }}
                            />
                          </div>
                        </div>

                        <div
                          className="kyc-card-head"
                          style={{
                            marginTop: "22px",
                          }}
                        >
                          <h4>Business Documents</h4>

                          <p>
                            Upload the requested documents. Our team will verify
                            them manually.
                          </p>
                        </div>

                        <div className="row g-3">
                          <UploadBox
                            id="panFrontBiz"
                            fileKey="panFrontBiz"
                            title="PAN Card"
                            description="Business PAN card"
                            accept=".jpg,.jpeg,.png"
                            icon="bi-file-earmark-image"
                          />

                          <UploadBox
                            id="bizProof"
                            fileKey="bizProof"
                            title="Business Proof"
                            description="GST / Incorporation / Udyam"
                            accept=".jpg,.jpeg,.png,.pdf"
                            icon="bi-buildings"
                          />

                          <UploadBox
                            id="authId"
                            fileKey="authId"
                            title="Authorised Person ID"
                            description="Aadhaar / Passport / DL"
                            accept=".jpg,.jpeg,.png"
                            icon="bi-person-vcard"
                          />

                          <UploadBox
                            id="addrProof"
                            fileKey="addrProof"
                            title="Address Proof"
                            description="Utility bill / bank statement"
                            accept=".jpg,.jpeg,.png,.pdf"
                            icon="bi-house"
                            optional
                          />

                          <UploadBox
                            id="gstImage"
                            fileKey="gstImage"
                            title="GST Certificate"
                            description="Upload GST certificate"
                            accept=".jpg,.jpeg,.png,.pdf"
                            icon="bi-file-earmark-text"
                            optional
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* =================================================
                      MANUAL REVIEW NOTE
                  ================================================= */}

                  <div
                    className="kyc-verify-box kyc-note"
                    style={{
                      marginTop: "22px",
                    }}
                  >
                    <div className="d-flex align-items-start gap-2">
                      <i
                        className="bi bi-info-circle-fill"
                        style={{
                          color: "var(--primary)",
                          marginTop: "1px",
                        }}
                      ></i>

                      <p
                        style={{
                          color: "var(--t2)",
                          fontSize: "12.5px",
                          lineHeight: "1.6",
                          margin: "0",
                        }}
                      >
                        KYC is completed <b>manually by our team</b>. Once you
                        submit your details and documents, our verification team
                        reviews everything. No automatic OTP or third-party
                        verification is used for this submission.
                      </p>
                    </div>
                  </div>

                  {/* =================================================
                      NAV
                  ================================================= */}

                  <div className="kyc-nav">
                    <button
                      type="button"
                      className="kyc-btn"
                      onClick={handleBack}
                      disabled={submitting}
                    >
                      <i className="bi bi-arrow-left"></i> Back
                    </button>

                    <button
                      type="button"
                      className="kyc-btn primary"
                      onClick={handleNext}
                      disabled={submitting}
                    >
                      Review & Submit <i className="bi bi-arrow-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* =================================================
                STEP 3 - REVIEW
            ================================================= */}

            {currentStep === 3 && (
              <div className="card-base kyc-panel on" data-panel="3">
                <div className="kyc-card">
                  <div className="kyc-card-head">
                    <h4>Review & Submit</h4>

                    <p>
                      Please confirm the details and documents below are correct
                      before submitting.
                    </p>
                  </div>

                  <div className="kyc-review-row">
                    <span>Business Type</span>

                    <b>{reviewType}</b>
                  </div>

                  <div className="kyc-review-row">
                    <span>Name</span>

                    <b>{reviewName || "—"}</b>
                  </div>

                  <div className="kyc-review-row">
                    <span>Contact</span>

                    <b>{reviewContact || "—"}</b>
                  </div>

                  <div className="kyc-review-row">
                    <span>PAN Uploaded</span>

                    <b>{hasPan ? "Yes" : "No"}</b>
                  </div>

                  <div className="kyc-review-row">
                    <span>Other Documents</span>

                    <b>{getReviewDocuments()}</b>
                  </div>

                  {formData.businessType === "business" && (
                    <>
                      <div className="kyc-review-row">
                        <span>Business Name</span>

                        <b>{formData.legalBusinessName || "—"}</b>
                      </div>

                      <div className="kyc-review-row">
                        <span>Registration No.</span>

                        <b>{formData.registrationNumber || "—"}</b>
                      </div>

                      <div className="kyc-review-row">
                        <span>GST Number</span>

                        <b>{formData.gstNumber || "—"}</b>
                      </div>
                    </>
                  )}

                  <div
                    className="kyc-verify-box kyc-note"
                    style={{
                      marginTop: "20px",
                    }}
                  >
                    <div className="d-flex align-items-start gap-2">
                      <i
                        className="bi bi-shield-check"
                        style={{
                          color: "var(--primary)",
                        }}
                      ></i>

                      <p
                        style={{
                          color: "var(--t2)",
                          fontSize: "12.5px",
                          lineHeight: "1.6",
                          margin: "0",
                        }}
                      >
                        By submitting, you confirm that the information and
                        documents provided by you are accurate and belong to the
                        account being verified.
                      </p>
                    </div>
                  </div>

                  <div className="kyc-nav">
                    <button
                      type="button"
                      className="kyc-btn"
                      onClick={handleBack}
                      disabled={submitting}
                    >
                      <i className="bi bi-arrow-left"></i> Back
                    </button>

                    <button
                      type="button"
                      className="kyc-btn primary"
                      id="kycSubmit"
                      onClick={submitKyc}
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                          ></span>
                          Submitting...
                        </>
                      ) : (
                        <>
                          Submit for Manual Review{" "}
                          <i className="bi bi-send"></i>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* =================================================
                STEP 4 - STATUS
            ================================================= */}

            {currentStep === 4 && (
              <div className="card-base kyc-panel on" data-panel="4">
                <div className="kyc-card">
                  <div className="kyc-status">
                    {/* =================================================
                        APPROVED
                    ================================================= */}

                    {isApproved ? (
                      <>
                        <div className="kyc-status-ico approved">
                          <i className="bi bi-patch-check-fill"></i>
                        </div>

                        <h3>KYC Approved</h3>

                        <span className="badge-lg approved">Verified</span>

                        <p>
                          Your KYC has been successfully verified. Your account
                          has full access to the available shipping features.
                        </p>

                        <div className="kyc-status-list">
                          <div className="s-row">
                            <span className="ic on">
                              <i className="bi bi-check-lg"></i>
                            </span>

                            <span className="st">Details Submitted</span>

                            <span className="tm">Completed</span>
                          </div>

                          <div className="s-row">
                            <span className="ic on">
                              <i className="bi bi-check-lg"></i>
                            </span>

                            <span className="st">Manual Review</span>

                            <span className="tm">Completed</span>
                          </div>

                          <div className="s-row">
                            <span className="ic on">
                              <i className="bi bi-patch-check"></i>
                            </span>

                            <span className="st">Approved</span>

                            <span className="tm">Verified</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* =================================================
                            PENDING
                        ================================================= */}

                        <div className="kyc-status-ico pending">
                          <i className="bi bi-hourglass-split"></i>
                        </div>

                        <h3>Application Submitted</h3>

                        <span className="badge-lg pending">Pending Review</span>

                        <p>
                          {successMessage ||
                            "Your KYC has been submitted successfully. Our team will review your documents manually."}
                        </p>

                        <div className="kyc-status-list">
                          <div className="s-row">
                            <span className="ic on">
                              <i className="bi bi-check-lg"></i>
                            </span>

                            <span className="st">Details Submitted</span>

                            <span className="tm">Completed</span>
                          </div>

                          <div className="s-row">
                            <span className="ic cur">
                              <i className="bi bi-clock"></i>
                            </span>

                            <span className="st">Manual Review</span>

                            <span className="tm">In review</span>
                          </div>

                          <div className="s-row">
                            <span className="ic off">
                              <i className="bi bi-patch-check"></i>
                            </span>

                            <span className="st">Approved</span>

                            <span className="tm">—</span>
                          </div>
                        </div>
                      </>
                    )}

                    {/* =================================================
                        ACTIONS
                    ================================================= */}

                    <div
                      className="kyc-nav"
                      style={{
                        justifyContent: "center",
                      }}
                    >
                      <a href="/" className="kyc-btn primary">
                        <i className="bi bi-grid-1x2"></i> Go to Dashboard
                      </a>

                      <a href="/support" className="kyc-btn">
                        <i className="bi bi-headset"></i> Contact Support
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Header>

      <Footer />
    </div>
  );
};

export default Kyc;
